/**
 * @fileoverview Shared bulk template analysis — awareness summary for dual-role templates.
 * @module inspection/bulkTemplateAnalysis
 *
 * Responsibilities:
 *   - Scan template forms and fields to produce a human-readable awareness
 *     summary of items that will NOT be populated during bulk inspections.
 *   - Classify field types as processable, skipped, or requiring-awareness.
 *   - Identify required fields without defaults so the template author can
 *     acknowledge that those requirements will not be enforced during bulk.
 *   - Detect parameter constraints (signatures, self-certification) that are
 *     irrelevant during bulk and surface them in the summary.
 *
 * Dependencies: none (pure functions, zero runtime dependencies).
 *
 * Architecture:
 *   - Database-agnostic analysis. Operates on canonical InspectionForm arrays
 *     and TemplateParameters-like objects so it can be consumed by tenant-web
 *     (template builder UI) and mobile-pwa (selection-time summary) alike.
 *   - Does NOT block templates from being marked bulk-eligible — it produces
 *     informational awareness items, not validation errors.
 *
 * Notes:
 *   - This is awareness, not restriction. Templates serve a dual role:
 *     single-parcel (collects everything) and bulk (skips certain items).
 *   - The caller decides how to render the summary. The analysis is pure data.
 */

import type { FieldType, InspectionFormField } from './types';

// ---------------------------------------------------------------------------
// Field type classification
// ---------------------------------------------------------------------------

/** Field types whose values can be automatically applied from defaults. */
const BULK_PROCESSABLE_FIELD_TYPES: ReadonlySet<FieldType> = new Set([
  'toggle', 'text', 'textarea', 'number', 'measurement',
  'dropdown', 'radioGroup', 'checkboxGroup',
  'date', 'dateTime',
]);

/** Field types that produce no response (display-only). */
const BULK_DISPLAY_ONLY_FIELD_TYPES: ReadonlySet<FieldType> = new Set([
  'instructions', 'section',
]);

/** Field types that will be skipped during bulk — awareness items. */
const BULK_SKIPPED_FIELD_TYPES: ReadonlySet<FieldType> = new Set([
  'camera', 'photo', 'signature', 'location',
  'repeatableList', 'inspectors', 'dataBound',
]);

/**
 * Human-readable labels for skipped field types, used in the awareness summary.
 * Keyed by FieldType; only types in BULK_SKIPPED_FIELD_TYPES are listed.
 */
const SKIPPED_FIELD_TYPE_LABELS: Partial<Record<FieldType, string>> = {
  camera: 'Photos will not be captured',
  photo: 'Photos will not be captured',
  signature: 'Signatures will not be collected',
  location: 'GPS readings will not be taken',
  repeatableList: 'Repeatable list rows will not be created',
  inspectors: 'Inspector assignments will not be populated',
  dataBound: 'Entity/case data will not be updated through the form',
};

// ---------------------------------------------------------------------------
// Analysis input / output types
// ---------------------------------------------------------------------------

/**
 * @contract BulkAnalysisForm
 * Purpose: Minimal form shape required for bulk template analysis.
 * Fields:  form identity, kind, module config, and fields array.
 * Rules:   Callers may pass full InspectionForm or a raw Dexie row with these fields.
 * Sync:    Read-only analysis input; never persisted.
 */
export interface BulkAnalysisForm {
  _id: string;
  name?: string;
  formKind?: string;
  moduleConfig?: { moduleType?: string };
  fields: ReadonlyArray<Pick<InspectionFormField, 'id' | 'type' | 'label' | 'required' | 'defaultValue' | 'extras'>>;
}

/**
 * @contract BulkAnalysisParams
 * Purpose: Minimal template parameters shape for bulk analysis.
 * Fields:  signature and self-certification flags.
 * Rules:   Optional — analysis degrades gracefully when absent.
 * Sync:    Read-only analysis input.
 */
export interface BulkAnalysisParams {
  requireInspectorSignature?: boolean;
  requireResponsiblePartySignature?: boolean;
  selfCertificationEnabled?: boolean;
}

/** A single informational item in the bulk awareness summary. */
export interface BulkAwarenessItem {
  /** Category for grouping: 'skippedField' | 'skippedModule' | 'requiredNoDefault' | 'parameterNote' */
  category: 'skippedField' | 'skippedModule' | 'requiredNoDefault' | 'parameterNote';
  /** Human-readable description shown to the template author. */
  message: string;
  /** Optional severity hint for UI styling. */
  severity: 'info' | 'warning';
  /** The form name where the item was found, if applicable. */
  formName?: string;
  /** The field label, if applicable. */
  fieldLabel?: string;
  /** The field type, if applicable. */
  fieldType?: string;
}

/**
 * @contract BulkTemplateAnalysisResult
 * Purpose: Complete awareness analysis of a template's bulk inspection readiness.
 * Fields:  items array, convenience counters, and a short summary string.
 * Rules:   Empty items array means no awareness items — template is fully processable in bulk.
 * Sync:    Ephemeral; used only in the builder UI and mobile selection summary.
 */
export interface BulkTemplateAnalysisResult {
  items: BulkAwarenessItem[];
  /** Count of fields that will be skipped (camera, photo, signature, etc.). */
  skippedFieldCount: number;
  /** Count of modules that will be skipped (contacts, violations). */
  skippedModuleCount: number;
  /** Count of required fields without a configured default value. */
  requiredNoDefaultCount: number;
  /** Count of parameter notes (signatures, self-cert). */
  parameterNoteCount: number;
  /** Short human-readable summary for display in compact UI. */
  summary: string;
}

// ---------------------------------------------------------------------------
// Field default resolution (mirrors inspection-renderer/fieldDefaults.ts)
// ---------------------------------------------------------------------------

function hasConfiguredDefault(field: Pick<InspectionFormField, 'type' | 'defaultValue' | 'extras'>): boolean {
  if (field.defaultValue != null) return true;
  const extras = (field.extras ?? {}) as Record<string, unknown>;
  switch (field.type) {
    case 'toggle':
      return extras.defaultState === 'on' || extras.defaultState === 'off';
    case 'date':
      return extras.defaultToToday === true;
    case 'dateTime':
      return extras.defaultToNow === true;
    default:
      return false;
  }
}

// ---------------------------------------------------------------------------
// Core analysis function
// ---------------------------------------------------------------------------

/**
 * @process analyzeBulkTemplateReadiness
 *
 * Steps:
 * 1. Iterate over forms — classify by formKind and moduleType.
 * 2. For standard forms, iterate fields and classify by field type.
 * 3. For required processable fields without defaults, add awareness item.
 * 4. For skipped field types, add awareness item (deduplicated by type per form).
 * 5. Check template parameters for signature/self-cert flags.
 * 6. Build summary string.
 *
 * Inputs:  forms array, optional template parameters.
 * Outputs: BulkTemplateAnalysisResult with all awareness items.
 * Failure Handling: Empty forms array produces empty result (no items).
 * Idempotency: REQUIRED — same inputs always produce the same result.
 */
export function analyzeBulkTemplateReadiness(
  forms: ReadonlyArray<BulkAnalysisForm>,
  params?: BulkAnalysisParams,
): BulkTemplateAnalysisResult {
  const items: BulkAwarenessItem[] = [];

  for (const form of forms) {
    const formName = form.name ?? 'Unnamed Form';

    // Module forms
    if (form.formKind === 'module') {
      const moduleType = form.moduleConfig?.moduleType;
      if (moduleType === 'violations') {
        items.push({
          category: 'skippedModule',
          message: 'Additional violations will not be logged during bulk inspections.',
          severity: 'info',
          formName,
        });
      } else if (moduleType === 'contacts') {
        items.push({
          category: 'skippedModule',
          message: 'Contact data collection will be handled separately from form responses during bulk inspections.',
          severity: 'info',
          formName,
        });
      }
      continue;
    }

    // Header forms — display-only, no awareness needed
    if (form.formKind === 'header') continue;

    // Standard forms — analyze fields
    const seenSkippedTypes = new Set<FieldType>();

    for (const field of form.fields) {
      // Display-only fields — no action
      if (BULK_DISPLAY_ONLY_FIELD_TYPES.has(field.type)) continue;

      // Skipped field types — deduplicate per type per form
      if (BULK_SKIPPED_FIELD_TYPES.has(field.type)) {
        if (!seenSkippedTypes.has(field.type)) {
          seenSkippedTypes.add(field.type);
          items.push({
            category: 'skippedField',
            message: SKIPPED_FIELD_TYPE_LABELS[field.type] ?? `${field.type} fields will not be populated.`,
            severity: 'info',
            formName,
            fieldType: field.type,
          });
        }
        continue;
      }

      // Processable fields — check for required without default
      if (BULK_PROCESSABLE_FIELD_TYPES.has(field.type)) {
        if (field.required && !hasConfiguredDefault(field)) {
          items.push({
            category: 'requiredNoDefault',
            message: `"${field.label}" is marked required but has no default value. This requirement will not be enforced during bulk inspections.`,
            severity: 'warning',
            formName,
            fieldLabel: field.label,
            fieldType: field.type,
          });
        }
      }
    }
  }

  // Parameter-level notes
  if (params?.requireInspectorSignature) {
    items.push({
      category: 'parameterNote',
      message: 'Inspector signature requirement will not be enforced during bulk inspections.',
      severity: 'info',
    });
  }
  if (params?.requireResponsiblePartySignature) {
    items.push({
      category: 'parameterNote',
      message: 'Responsible party signature requirement will not be enforced during bulk inspections.',
      severity: 'info',
    });
  }
  if (params?.selfCertificationEnabled) {
    items.push({
      category: 'parameterNote',
      message: 'Self-certification will not be applicable during bulk inspections.',
      severity: 'info',
    });
  }

  // Counts
  const skippedFieldCount = items.filter((i) => i.category === 'skippedField').length;
  const skippedModuleCount = items.filter((i) => i.category === 'skippedModule').length;
  const requiredNoDefaultCount = items.filter((i) => i.category === 'requiredNoDefault').length;
  const parameterNoteCount = items.filter((i) => i.category === 'parameterNote').length;

  // Summary
  const parts: string[] = [];
  if (skippedFieldCount > 0 || skippedModuleCount > 0) {
    const total = skippedFieldCount + skippedModuleCount;
    parts.push(`${total} item${total === 1 ? '' : 's'} will be skipped`);
  }
  if (requiredNoDefaultCount > 0) {
    parts.push(`${requiredNoDefaultCount} required field${requiredNoDefaultCount === 1 ? '' : 's'} without defaults`);
  }
  if (parameterNoteCount > 0) {
    parts.push(`${parameterNoteCount} parameter${parameterNoteCount === 1 ? '' : 's'} not enforced`);
  }
  const summary = parts.length > 0 ? parts.join(' · ') : 'All fields are fully processable in bulk mode.';

  return {
    items,
    skippedFieldCount,
    skippedModuleCount,
    requiredNoDefaultCount,
    parameterNoteCount,
    summary,
  };
}
